import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  autoUpdate,
  flip,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarEdit } from '@fortawesome/pro-regular-svg-icons';

import InputField from 'components/FormFields/InputField';
import CalendarPicker from 'components/CalendarPicker';

import dayjs from 'dayjs';

import './calendar-widget.scss';

const CalendarWidget = ({
  name,
  date,
  pickerDate,
  dateFormatLabel = 'MM/DD/YYYY',
  setDate,
  placeholder = '',
  showLabel,
  showError,
  showNextYears,
  defaultPlacement = 'bottom-start',
  datePreference = 'MM/DD/YYYY',
  onPickerClose,
  defaultValue = '',
  disabled,
  hasClear = true,
  adjustOnVerticalOverflow = true,
  useDismissProps = {},
  nPastYears,
  nNextYears,
  showReminder,
  entityId,
  reminders,
  createReminder,
  updateReminder,
  deleteReminder,
  shouldCloseOnSelect = true,
  ...restProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dismiss = () => {
    setIsOpen(false);
    onPickerClose?.call();
  };

  const show = () => {
    setIsOpen(true);
  };

  let flipOptions = {
    fallbackPlacements: ['top', 'bottom'],
  };

  if (adjustOnVerticalOverflow) {
    flipOptions = { ...flipOptions, rootBoundary: 'viewport' };
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    placement: defaultPlacement,
    middleware: [
      offset(),
      flip(flipOptions),
      size({
        apply({ elements }) {
          Object.assign(elements.floating.style, {
            height: `${elements.floating.scrollHeight}px`,
          });
        },
      }),
    ],
    onOpenChange(opened, event) {
      if (!disabled && opened) {
        show(event);
      } else {
        dismiss(event);
      }
    },
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const close = useDismiss(context, useDismissProps);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    close,
  ]);

  const handleDateChange = (d) => {
    if (!d) return;
    const newDate = new Date(d);
    setDate(dayjs(newDate).format(dateFormatLabel));

    if (shouldCloseOnSelect) {
      setIsOpen(false);
      onPickerClose?.call();
    }
  };

  const handleFieldChange = (event) => {
    const d = event.target.value;
    setDate(d);
  };

  const handleDateClear = () => {
    setDate('');
    setIsOpen(true);
  };

  const id = name.replace(/ /g, '-').toLowerCase();

  return (
    <>
      <InputField
        ref={refs.setReference}
        name={name}
        id={id}
        label={name}
        autoComplete="off"
        value={date || defaultValue}
        showLabel={showLabel}
        showError={showError}
        onChange={handleFieldChange}
        placeholder={placeholder}
        wrapperProps={{
          className: 'form-item--calendarpicker',
        }}
        {...getReferenceProps()}
        {...restProps}
      >
        <FontAwesomeIcon
          className="form-item__icon"
          icon={faCalendarEdit}
          style={{
            pointerEvents: 'none',
          }}
        />
      </InputField>
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={{ ...floatingStyles, zIndex: 1001 }}
          onMouseDown={(e) => e.preventDefault()} // Prevents blurring the input when clicking calendar.
          {...getFloatingProps()}
        >
          <CalendarPicker
            selected={pickerDate || date}
            showReminder={showReminder}
            showNextYears={showNextYears}
            onDateClear={handleDateClear}
            onDateChange={handleDateChange}
            hasClear={hasClear}
            closeCalendar={() => {}}
            nPastYears={nPastYears}
            nNextYears={nNextYears}
            entityId={entityId}
            reminders={reminders}
            createReminder={createReminder}
            updateReminder={updateReminder}
            deleteReminder={deleteReminder}
            datePreference={datePreference}
          />
        </div>
      )}
    </>
  );
};

CalendarWidget.propTypes = {
  name: PropTypes.string,
  date: PropTypes.any,
  pickerDate: PropTypes.any,
  dateFormatLabel: PropTypes.string,
  setDate: PropTypes.func,
  placeholder: PropTypes.string,
  showLabel: PropTypes.bool,
  showError: PropTypes.bool,
  showNextYears: PropTypes.bool,
  defaultPlacement: PropTypes.string,
  onPickerClose: PropTypes.func,
  defaultValue: PropTypes.string,
  hasClear: PropTypes.bool,
  shouldCloseOnSelect: PropTypes.bool,
};

export default CalendarWidget;
